import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { showAlert } from '../../../../utilities/notifications';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { CARD_CRYPTODRAFT_DEPOSIT } from '../../mutations';
import { CARD_LINKED_WALLETS } from '../../queries';
import { cryptodraftScreens, forceUpdateOptions } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';

const ReviewExtendOffer = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const { state, openScreen, forceUpdateAction } = useContext(CryptodraftContext);

  const [ltvRangesAndInterest, setLtvRangesAndInterest] = useState([]);

  const isUSUser = state?.user?.isUS;
  const collateralAmount = state?.collateralAmount;
  const cryptodraftDetails = useMemo(
    () => state?.cryptodraftDetails ?? {},
    [state?.cryptodraftDetails]
  );
  const fiatCurrency = state?.fiatCurrency;
  const principalCurrency = state?.principalCurrency;
  const cryptodraftTierLTV = state?.cryptodraftTierLTV;

  const {
    cryptodraft_ltv_ranges: _cryptodraftLtvRanges,
    cryptodraft_interest_mpr: _cryptodraftInterestMpr,
    cryptodraft_us_ltv_ranges: _cryptodraftUSLtvRanges,
    cryptodraft_us_interest_mpr: _cryptodraftUSInterestMpr,
  } = state?.cryptodraftSettings ?? {
    cryptodraft_ltv_ranges: '',
    cryptodraft_interest_mpr: '',
    cryptodraft_us_ltv_ranges: '',
    cryptodraft_us_interest_mpr: '',
  };

  const cryptodraftPrincipleToDispalyCurrencyRate = useRate(fiatCurrency, principalCurrency);

  const cryptodraftCollateralCurrencyToDisplayCurrencyRate = useRate(
    cryptodraftDetails?.collateral_currency,
    principalCurrency
  );

  const { refetch: getLinkedWallet } = useQuery(CARD_LINKED_WALLETS, {
    enabled: false,
  });

  const rate = useRate(cryptodraftDetails?.collateral_currency, fiatCurrency);

  const { mutate: createCryptodraft, isLoading } = useMutation(CARD_CRYPTODRAFT_DEPOSIT, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        getLinkedWallet();
        forceUpdateAction(forceUpdateOptions.DETAILS, true);
        showAlert({ message: translate('extend_cryptodraft_success_msg'), type: 'success' });
        history.push('/dashboard/card/details');
      }
    },
  });

  const totalCollateralAmount = useMemo(() => {
    if (cryptodraftDetails && cryptodraftDetails?.collateral_currency) {
      return (
        collateralAmount +
        cryptodraftDetails?.collateral_amount *
          currencies[cryptodraftDetails?.collateral_currency?.toUpperCase()].conversion
      );
    }
    return 0;
  }, [cryptodraftDetails, collateralAmount]);

  const cryptodraftLtvRanges = useMemo(
    () => (isUSUser ? _cryptodraftUSLtvRanges : _cryptodraftLtvRanges),
    [_cryptodraftLtvRanges, _cryptodraftUSLtvRanges, isUSUser]
  );

  const cryptodraftInterestMpr = useMemo(
    () => (isUSUser ? _cryptodraftUSInterestMpr : _cryptodraftInterestMpr),
    [_cryptodraftUSInterestMpr, _cryptodraftInterestMpr, isUSUser]
  );

  const topLtv = useMemo(() => {
    if (ltvRangesAndInterest.length > 0)
      return ltvRangesAndInterest[ltvRangesAndInterest.length - 1]?.end;
    return 10;
  }, [ltvRangesAndInterest]);

  const calculateAvailableSpend = (ltvRangesString, index, mprPercent) => {
    if (!cryptodraftTierLTV || isUSUser) {
      let availableSpend = 0;
      if (index === 0) {
        availableSpend =
          formatNumber(totalCollateralAmount * rate, exchange.FIAT_VALUE) * (mprPercent / 100);
      } else {
        const highLTV = ltvRangesString.split(',')[index - 1].split('-')[1];
        availableSpend =
          formatNumber(totalCollateralAmount * rate, exchange.FIAT_VALUE) *
          ((mprPercent - highLTV) / 100);
      }
      return availableSpend;
    }

    if (cryptodraftTierLTV < topLtv) {
      let availableSpend = 0;
      if (index === 0) {
        availableSpend =
          formatNumber(totalCollateralAmount * rate, exchange.FIAT_VALUE) * (mprPercent / 100);
      } else {
        const lowLTV = cryptodraftTierLTV;

        availableSpend =
          formatNumber(totalCollateralAmount * rate, exchange.FIAT_VALUE) *
          ((topLtv - lowLTV) / 100);
      }
      return availableSpend;
    }
    return formatNumber(totalCollateralAmount * rate, exchange.FIAT_VALUE) * (mprPercent / 100);
  };

  const parseLTVRanges = (ltvRangesString, interests) => {
    const interestValues = interests.split(',');
    const ranges = ltvRangesString.split(',').map((rangeString, index) => {
      const rangeArray = rangeString.split('-');

      return {
        index,
        start: Number(rangeArray[0]),
        end: rangeArray.length > 1 ? Number(rangeArray[1]) : Number(100),
        interest: interestValues[index],
        availableSpend: calculateAvailableSpend(
          ltvRangesString,
          index,
          rangeArray.length > 1 ? Number(rangeArray[1]) : Number(100)
        ),
      };
    });
    return ranges;
  };

  useEffect(() => {
    setLtvRangesAndInterest(parseLTVRanges(cryptodraftLtvRanges, cryptodraftInterestMpr));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptodraftLtvRanges, cryptodraftInterestMpr]);

  const finalLTVRangesAndInterest = useMemo(() => {
    if (!cryptodraftTierLTV || isUSUser) {
      return ltvRangesAndInterest;
    }
    if (cryptodraftTierLTV && topLtv && ltvRangesAndInterest) {
      if (cryptodraftTierLTV < topLtv) {
        const newStartLtvRange = {
          index: 0,
          start: 0,
          end: cryptodraftTierLTV,
          interest: cryptodraftInterestMpr[0],
          availableSpend: calculateAvailableSpend(cryptodraftLtvRanges, 0, cryptodraftTierLTV),
        };

        const ltvRangesWithoutLtvLessThanTier = ltvRangesAndInterest.filter(
          (item) => item.end > cryptodraftTierLTV
        );

        if (ltvRangesWithoutLtvLessThanTier && ltvRangesWithoutLtvLessThanTier?.length > 0) {
          ltvRangesWithoutLtvLessThanTier[0].start = cryptodraftTierLTV + 0.01;
          ltvRangesWithoutLtvLessThanTier[0].availableSpend = calculateAvailableSpend(
            cryptodraftLtvRanges,
            1,
            cryptodraftTierLTV
          );
        }

        return [newStartLtvRange, ...ltvRangesWithoutLtvLessThanTier];
      }

      const newStartLtvRange = {
        index: 0,
        start: 0,
        end: cryptodraftTierLTV,
        interest: cryptodraftInterestMpr[0],
        availableSpend: calculateAvailableSpend(cryptodraftLtvRanges, 0, cryptodraftTierLTV),
      };
      return [newStartLtvRange];
    }
    return ltvRangesAndInterest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cryptodraftTierLTV,
    ltvRangesAndInterest,
    topLtv,
    isUSUser,
    cryptodraftInterestMpr,
    cryptodraftLtvRanges,
  ]);

  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() => openScreen(cryptodraftScreens.CALCULATOR)}
      title={translate('cryptodraft_activate_review_offer_label')}>
      <p className="text-dark text-f6 mt-4 pt-3" style={{ lineHeight: '1rem' }}>
        {translate('extend_cryptodraft_available_spending_credit_note')}
      </p>
      <div className="mt-2">
        <b className="text-dark text-f3 p-0">{`
          ${formatNumberWithComma(
            formatNumber(
              formatNumber(
                totalCollateralAmount * cryptodraftCollateralCurrencyToDisplayCurrencyRate,
                exchange.FIAT_VALUE
              ) *
                (topLtv / 100),
              exchange.FIAT_VALUE
            )
          )}
        ${principalCurrency?.toUpperCase()}`}</b>
      </div>
      <div className="d-flex flex-row mt-4 rounded-extra bg-lighter p-3">
        <p className="text-dark text-f6 d-flex align-items-center">
          {translate('extend_cryptodraft_additional_collateral')}
        </p>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {formatNumber(collateralAmount, exchange.CRYPTO_VALUE)}{' '}
            {cryptodraftDetails?.collateral_currency?.toUpperCase()}
          </b>
          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(formatNumber(collateralAmount * rate, exchange.FIAT_VALUE))}{' '}
            {fiatCurrency}
          </p>
        </span>
      </div>
      <div className="d-flex flex-row mt-3 rounded-extra bg-lighter p-3">
        <p className="text-dark text-f6 d-flex align-items-center">
          {translate('extend_cryptodraft_new_collateral_balance')}
        </p>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {formatNumber(totalCollateralAmount, exchange.CRYPTO_VALUE)}{' '}
            {cryptodraftDetails?.collateral_currency?.toUpperCase()}
          </b>
          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(formatNumber(totalCollateralAmount * rate, exchange.FIAT_VALUE))}{' '}
            {fiatCurrency}
          </p>
        </span>
      </div>
      <div className="mt-4 pb-1">
        <p className="text-shadow text-f6 p-0">
          {translate('cryptodraft_calculator_remaining_spend_note')}
        </p>
      </div>
      <div className="rounded-extra bg-lighter mt-2 mb-3 card-calculator__summary">
        <div className="d-flex flex-column p-0">
          {finalLTVRangesAndInterest.map((item) => (
            <React.Fragment key={item.index}>
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <p className="card-calculator__summary--item-note text-dark text-f6">
                    {translate('cryptodraft_calculator_mpr_note')
                      .replace('%d1', `${item.start}`)
                      .replace('%d2', `-${item.end}`)
                      .replace('%d3', `${item.interest}`)}
                  </p>
                  <b className="text-f6">
                    {` ${formatNumber(
                      item.availableSpend * cryptodraftPrincipleToDispalyCurrencyRate,
                      exchange.FIAT_VALUE
                    )} ${principalCurrency?.toUpperCase()}`}
                  </b>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="btn-container">
        <button
          type="button"
          className="btn py-3 btn-primary w-100"
          loading={isLoading ? 'true' : 'false'}
          disabled={collateralAmount <= 0}
          onClick={() => {
            createCryptodraft({ amount: collateralAmount });
          }}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default ReviewExtendOffer;
